import { useMemo } from "react";
import { QueryClient, useQuery } from "@tanstack/react-query";

import type { APII } from "@/api/glencoco";
import {
  MANAGE_FEATURES,
  ManageFeaturesI,
  ManageMeI,
  ManageMePodI,
  ManageMeWorkspaceI,
} from "@/api/routes/manage";
import { useApiClient } from "@/context/api-client";
import { isManageSwitchedOff } from "@/lib/manage/errors";
import {
  hasManageRoles,
  isManageLga,
  isManagePodLead,
} from "@/lib/manage/roles";

/**
 * What the signed-in user may manage (C-14, `GET /user/v1/manage/me`). One
 * hook for every manage surface: nav, redirects, the Admin page and F3's
 * block pages. The server enforces every route; this only decides what to
 * show, and fails closed: loading, off and any error show nothing new.
 */

export const MANAGE_ME_QUERY_KEY = ["manage", "me"] as const;

export interface ManageMeResultI {
  /** False while the backend's `user_roles_enabled` switch is off. */
  isSwitchedOn: boolean;
  me: ManageMeI | null;
}

// A backend without `features` (before C-14 phase 2) switches every
// phase-2 page off: only an explicit true shows one.
const normalizeFeatures = (
  features: Partial<ManageFeaturesI> | undefined
): ManageFeaturesI =>
  Object.values(MANAGE_FEATURES).reduce(
    (all, key) => ({ ...all, [key]: features?.[key] === true }),
    // Optional (C-16 B-1): present only when the backend turns it on.
    (features?.all_workspaces_health === true
      ? { all_workspaces_health: true }
      : {}) as ManageFeaturesI
  );

const normalizeMe = (data: Partial<ManageMeI> | undefined): ManageMeI => ({
  roles: Array.isArray(data?.roles) ? data.roles : [],
  is_admin: data?.is_admin === true,
  workspaces: Array.isArray(data?.workspaces) ? data.workspaces : [],
  pods: Array.isArray(data?.pods) ? data.pods : [],
  impersonating: data?.impersonating === true,
  features: normalizeFeatures(data?.features),
});

export const fetchManageMe = async (
  api: Pick<APII, "manage">
): Promise<ManageMeResultI> => {
  try {
    const { data } = await api.manage.getMe();

    return { isSwitchedOn: true, me: normalizeMe(data) };
  } catch (error) {
    if (isManageSwitchedOff(error)) {
      return { isSwitchedOn: false, me: null };
    }

    throw error;
  }
};

/** Shared by the hook and by callers outside ApiClientProvider (redirects). */
export const manageMeQueryOptions = (api: Pick<APII, "manage">) => ({
  queryKey: MANAGE_ME_QUERY_KEY,
  queryFn: () => fetchManageMe(api),
  staleTime: 5 * 60 * 1000,
  // A failure must never hold anyone up: no retries, the UI stays hidden.
  retry: false,
  refetchOnWindowFocus: false,
});

export const fetchManageMeQuery = (
  queryClient: QueryClient,
  api: Pick<APII, "manage">
) => queryClient.fetchQuery(manageMeQueryOptions(api));

export type ManageMeStatusI = "loading" | "off" | "on" | "error";

export interface UseManageMeI {
  status: ManageMeStatusI;
  /** Set only while `status` is "on". */
  me: ManageMeI | null;
  hasRoles: boolean;
  isAdmin: boolean;
  isLga: boolean;
  isPodLead: boolean;
  /** Workspaces the user can write in (LGA, or Admin through manage/me). */
  writeWorkspaces: Array<ManageMeWorkspaceI>;
  pods: Array<ManageMePodI>;
  impersonating: boolean;
  /** Asks manage/me again (the "Reload" on a failed load). */
  refetch: () => void;
  /** A request for manage/me is in flight (first load or a reload). */
  isFetching: boolean;
}

export const useManageMe = (): UseManageMeI => {
  const api = useApiClient();
  const { data, isError, isFetching, refetch } = useQuery(
    manageMeQueryOptions(api)
  );

  return useMemo(() => {
    let status: ManageMeStatusI = "loading";

    if (isError) {
      status = "error";
    } else if (data) {
      status = data.isSwitchedOn ? "on" : "off";
    }

    const me = status === "on" ? (data?.me ?? null) : null;

    return {
      status,
      me,
      hasRoles: hasManageRoles(me),
      isAdmin: !!me?.is_admin,
      isLga: isManageLga(me),
      isPodLead: isManagePodLead(me),
      writeWorkspaces: (me?.workspaces ?? []).filter(
        (workspace) => workspace.access === "write"
      ),
      pods: me?.pods ?? [],
      impersonating: !!me?.impersonating,
      refetch: () => {
        refetch();
      },
      isFetching,
    };
  }, [data, isError, isFetching, refetch]);
};
